import { Check, Package, ClipboardCheck, CalendarCheck, Truck, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

interface TimelineStage {
  id: string;
  label: string;
  icon: React.ElementType;
}

const STAGES: TimelineStage[] = [
  { id: "submitted", label: "Submitted", icon: Package },
  { id: "approved", label: "Approved", icon: ClipboardCheck },
  { id: "scheduled", label: "Pickup Scheduled", icon: CalendarCheck },
  { id: "picked_up", label: "Picked Up", icon: Truck },
  { id: "returned", label: "Returned", icon: RotateCcw },
];

type PickupStage = "submitted" | "approved" | "scheduled" | "picked_up" | "returned";

interface PickupStatusTimelineProps {
  currentStage: PickupStage;
  className?: string;
}

export const PickupStatusTimeline = ({ currentStage, className }: PickupStatusTimelineProps) => {
  const currentIndex = STAGES.findIndex((s) => s.id === currentStage);

  return (
    <div className={cn("w-full py-4", className)}>
      {/* Desktop: Horizontal Timeline */}
      <div className="hidden sm:flex items-center justify-between relative">
        {/* Connecting line (background) */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-border" />
        {/* Connecting line (progress) */}
        <div
          className="absolute top-5 left-0 h-0.5 bg-primary transition-all duration-500"
          style={{ width: `${(currentIndex / (STAGES.length - 1)) * 100}%` }}
        />

        {STAGES.map((stage, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const Icon = stage.icon;

          return (
            <div
              key={stage.id}
              className="relative z-10 flex flex-col items-center"
            >
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                  isCompleted
                    ? "bg-primary border-primary text-primary-foreground"
                    : isCurrent
                    ? "bg-primary/10 border-primary text-primary"
                    : "bg-card border-border text-muted-foreground"
                )}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
              </div>
              <span
                className={cn(
                  "mt-2 text-xs font-medium text-center max-w-[80px]",
                  isCompleted || isCurrent
                    ? "text-foreground"
                    : "text-muted-foreground"
                )}
              >
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Mobile: Vertical Timeline */}
      <div className="sm:hidden flex flex-col gap-1">
        {STAGES.map((stage, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const Icon = stage.icon;
          const isLast = index === STAGES.length - 1;

          return (
            <div key={stage.id} className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                    isCompleted
                      ? "bg-primary border-primary text-primary-foreground"
                      : isCurrent
                      ? "bg-primary/10 border-primary text-primary"
                      : "bg-card border-border text-muted-foreground"
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>
                {!isLast && (
                  <div
                    className={cn(
                      "w-0.5 h-6 transition-colors duration-300",
                      isCompleted ? "bg-primary" : "bg-border"
                    )}
                  />
                )}
              </div>
              <div className="pt-1">
                <span
                  className={cn(
                    "text-sm font-medium",
                    isCompleted || isCurrent
                      ? "text-foreground"
                      : "text-muted-foreground"
                  )}
                >
                  {stage.label}
                </span>
                {isCurrent && (
                  <span className="ml-2 text-xs text-primary font-medium">
                    (Current)
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
